"use client";

import { useState } from "react";
import { Form, FormField } from "@/app/_components/ui/form";
import { SignupFormValues } from "@/app/_lib/schemas/auth";
import { UseFormReturn } from "react-hook-form";
import { createuser } from "../lib/actions";
import { signIn } from "next-auth/react";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const inputCls = (hasError: boolean) =>
  `w-full px-4 py-3 text-sm border rounded-xl bg-background outline-none transition-colors ${
    hasError ? "border-red-400" : "border-border focus:border-primary"
  }`;

export default function Password({
  form,
}: {
  form: UseFormReturn<SignupFormValues>;
}) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const onSubmit = async (data: SignupFormValues) => {
    if (!data.firstName || !data.lastName) {
      if (!data.firstName) form.setError("firstName", { message: "Enter first name" });
      if (!data.lastName) form.setError("lastName", { message: "Enter last name" });
      return;
    }
    if (!data.password || !data.confirmPassword) {
      form.setError("password", { message: "Enter a password" });
      form.setError("confirmPassword", { message: "Confirm your password" });
      return;
    }
    if (data.password.length < 6) {
      form.setError("password", { message: "At least 6 characters" });
      return;
    }
    if (data.password !== data.confirmPassword) {
      form.setError("confirmPassword", { message: "Passwords don't match" });
      return;
    }

    try {
      setLoading(true);
      await createuser(data);
      await signIn("credentials", {
        email: data.email,
        password: data.password,
        redirect: true,
        callbackUrl: "/dashboard/messages",
      });
      form.resetField("password");
      form.resetField("confirmPassword");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-3.5 w-full"
      >
        {/* Name row */}
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1 flex-1">
                <input
                  type="text"
                  placeholder="First name"
                  {...field}
                  className={inputCls(!!fieldState.error)}
                />
                {fieldState.error && (
                  <span className="text-xs text-red-500">{fieldState.error.message}</span>
                )}
              </div>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1 flex-1">
                <input
                  type="text"
                  placeholder="Last name"
                  {...field}
                  className={inputCls(!!fieldState.error)}
                />
                {fieldState.error && (
                  <span className="text-xs text-red-500">{fieldState.error.message}</span>
                )}
              </div>
            )}
          />
        </div>

        {/* Password */}
        <FormField
          control={form.control}
          name="password"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1 relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                {...field}
                className={`${inputCls(!!fieldState.error)} pr-10`}
              />
              <button
                type="button"
                className="absolute right-3 top-3.5 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {fieldState.error && (
                <span className="text-xs text-red-500">{fieldState.error.message}</span>
              )}
            </div>
          )}
        />

        {/* Confirm password */}
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1 relative">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm password"
                {...field}
                className={`${inputCls(!!fieldState.error)} pr-10`}
              />
              <button
                type="button"
                className="absolute right-3 top-3.5 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {fieldState.error && (
                <span className="text-xs text-red-500">{fieldState.error.message}</span>
              )}
            </div>
          )}
        />

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 mt-1 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Creating account…
            </>
          ) : (
            "Create account"
          )}
        </button>
      </form>
    </Form>
  );
}
